import { create } from "zustand";
import { MOBILE_MAX_WIDTH } from "@/constants";
import { BlockNoteEditor } from "@blocknote/core";

type State = {
  alertWidgetAIContentEditWithAI?: {
    noteId?: string;
    title?: string;
    callback?: () => void;
  };
  alertOnboardingVisible?: boolean;
  alertAIEdits?: {
    onDeleteCallback?: () => void;
  };
  editor?: BlockNoteEditor;
  useRealtimeTranscription: {
    pendingText: string;
    finalText: string;
  };
  device: {
    isMobile: boolean;
    width: number;
    height: number;
    safeArea: {
      top: number;
      bottom: number;
    };
  };
  alertSelect?: {
    title?: string;
    buttonText?: string;
    defaultOption?: number;
    options: string[];
    onSelect?: (index: number) => void;
    onSelectAsync?: (index: number) => Promise<void>;
  };
  useUploadParams?: {
    fileType: string;
    key: string;
  };
  paywallVisible?: boolean;
  alertPromptSignIn?: {
    title: string;
    subtitle: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
    onGoogleSignIn?: () => void;
    onAppleSignIn?: () => void;
  };
  alertModal?: {
    title: string;
    loading?: boolean;
    subtitle: string;
    shouldPersistBackdropClick?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    onDismiss?: () => void;
    onLeftButtonPress?: () => void;
    button?: { text: string; onPress: () => void; destructive?: boolean };
    cancelText?: string;
  };
  alertPrompt?: {
    title: string;
    subtitle?: string;
    placeholder: string;
    shouldPersistBackdropClick?: boolean;
    multiLine?: boolean;
    loading?: boolean;
    hideDismissIcon?: boolean;
    hideCancel?: boolean;
    defaultValue?: string;
    button: {
      text: string;
      onPress: (text: string) => void;
      destructive?: boolean;
    };
  };
};

type Actions = {
  setDevice: (payload: State["device"]) => void;
  setAlertModal: (payload: State["alertModal"]) => void;
  setAlertAIEdits: (payload: State["alertAIEdits"]) => void;
  setAlertWidgetAIContentEditWithAI: (
    payload: State["alertWidgetAIContentEditWithAI"]
  ) => void;
  setAlertSelect: (payload: State["alertSelect"]) => void;
  setUseUploadParams: (payload: State["useUploadParams"]) => void;
  setEditor: (payload: State["editor"]) => void;
  setAlertPromptSignIn: (payload: State["alertPromptSignIn"]) => void;
  setAlertPrompt: (payload: State["alertPrompt"]) => void;
  setAlertOnboardingVisible: (payload: State["alertOnboardingVisible"]) => void;
  setPaywallVisible: (payload: State["paywallVisible"]) => void;
  setUseRealtimeTranscription: (
    payload: State["useRealtimeTranscription"]
  ) => void;
  resetUseRealtimeTranscription: () => void;
  reset: () => void;
};

// define the initial state
const initialState: State = {
  useRealtimeTranscription: {
    pendingText: "",
    finalText: "",
  },
  device: {
    isMobile: window.innerWidth < MOBILE_MAX_WIDTH,
    width: window.innerWidth,
    height: window.innerHeight,
    safeArea: {
      top: 0,
      bottom: 0,
    },
  },
};

export const useUISlice = create<State & Actions>()((set, get) => ({
  ...initialState,
  setDevice: (payload) => {
    set({ device: payload });
  },
  setAlertWidgetAIContentEditWithAI: (payload) => {
    set({ alertWidgetAIContentEditWithAI: payload });
  },
  setAlertAIEdits: (payload) => {
    set({ alertAIEdits: payload });
  },
  setEditor: (payload) => {
    set({ editor: payload });
  },
  setAlertSelect: (payload) => {
    set({ alertSelect: payload });
  },
  setUseUploadParams: (payload) => {
    set({ useUploadParams: payload });
  },
  setAlertPromptSignIn: (payload) => {
    set({ alertPromptSignIn: payload });
  },
  setAlertModal: (payload) => {
    set({ alertModal: payload });
  },
  setAlertPrompt: (payload) => {
    set({ alertPrompt: payload });
  },
  setAlertOnboardingVisible: (payload) => {
    set({ alertOnboardingVisible: payload });
  },
  setPaywallVisible: (payload) => {
    set({ paywallVisible: payload });
  },
  setUseRealtimeTranscription: (payload) => {
    set({ useRealtimeTranscription: payload });
  },
  resetUseRealtimeTranscription: () => {
    set({ useRealtimeTranscription: { pendingText: "", finalText: "" } });
  },
  reset: () => {
    set({ ...initialState });
  },
}));
